// @ts-nocheck
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import classNames from 'classnames';
import ShimmerButton from './ShimmerButton';
import ThemeCard from './ThemeCard';
import TagBubble from './TagBubble';
import { api } from '../services/api';
import { useAnalytics } from '../hooks/useAnalytics';
import { useABTest, useABTestConversion } from '../hooks/useABTest';
import { useVoiceInput } from '../hooks/useVoiceInput';
import { Theme, Tag } from '../types';

interface AICurationStepProps {
    onSubmit: (
        searchText: string,
        selectedThemes: Theme[],
        selectedTags: Tag[]
    ) => void;
    isProcessing: boolean;
}

// Processing messages for loading state
const PROCESSING_MESSAGES = [
    'Analyzing your artistic preferences...',
    'Curating your perfect artcast...',
    'Matching you with inspiring content...',
    'Finding artworks that speak to you...',
    'Creating your personalized experience...',
];

export default function AICurationStep({
    onSubmit,
    isProcessing,
}: AICurationStepProps) {
    const [searchText, setSearchText] = useState('');
    const [selectedThemes, setSelectedThemes] = useState<Theme[]>([]);
    const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
    const [themes, setThemes] = useState<Theme[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);
    const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [hasLoadedData, setHasLoadedData] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const analytics = useAnalytics();

    // Voice input hook
    const {
        transcript,
        listening,
        isSupported: voiceSupported,
        microphonePermission,
        permissionDenied,
        toggleListening,
    } = useVoiceInput();

    // Auto-adjust textarea height
    const adjustTextareaHeight = (element: HTMLTextAreaElement) => {
        element.style.height = 'auto';
        element.style.height = Math.min(element.scrollHeight, 128) + 'px'; // max 8rem (128px)
    };

    // Adjust height when searchText changes
    useEffect(() => {
        if (textareaRef.current) {
            adjustTextareaHeight(textareaRef.current);
        }
    }, [searchText]);

    // Update search text with voice transcript - simple implementation
    useEffect(() => {
        console.log('[AI-CURATION] Transcript effect triggered:', transcript);
        console.log('[AI-CURATION] Current searchText:', searchText);
        console.log('[AI-CURATION] Listening state:', listening);

        if (transcript) {
            console.log(
                '[AI-CURATION] Updating searchText with transcript:',
                transcript
            );
            setSearchText(transcript);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transcript]);

    // A/B Test for search placeholder
    const promptVariant = useABTest(
        'ai_curation_search',
        ['control', 'inspiring', 'creative', 'personalized'],
        'control'
    );
    const trackPromptConversion = useABTestConversion(
        'ai_curation_search',
        promptVariant
    );

    // Define different placeholder texts based on variant
    const placeholderTexts = {
        control: 'Search for artworks...',
        inspiring: 'What inspires you?',
        creative: 'Describe your creative vision...',
        personalized: "Tell us what you're looking for...",
    };

    // Fetch themes and tags
    useEffect(() => {
        console.log(
            '[PAIR-APP] useEffect triggered - hasLoadedData:',
            hasLoadedData
        );
        console.log('[PAIR-APP] api.getThemes exists?', typeof api.getThemes);
        console.log('[PAIR-APP] api.getTopTags exists?', typeof api.getTopTags);

        // Prevent multiple fetches
        if (hasLoadedData) {
            console.log('[PAIR-APP] Data already loaded, skipping fetch');
            return;
        }

        const fetchData = async () => {
            try {
                console.log('[PAIR-APP] Starting to fetch themes and tags...');
                setHasLoadedData(true);

                console.log('[PAIR-APP] Calling api.getThemes()...');
                console.log('[PAIR-APP] Calling api.getTopTags(20)...');

                // Fetch themes and tags in parallel
                const [themesResult, tagsResult] = await Promise.all([
                    api.getThemes(),
                    api.getTopTags(20),
                ]);

                console.log('[PAIR-APP] API calls completed!');
                console.log('[PAIR-APP] themesResult:', themesResult);
                console.log('[PAIR-APP] tagsResult:', tagsResult);

                if (themesResult.success && themesResult.data) {
                    console.log(
                        '[PAIR-APP] Setting themes:',
                        themesResult.data
                    );
                    setThemes(themesResult.data);
                    console.log(
                        '[PAIR-APP] Fetched themes count:',
                        themesResult.data.length
                    );
                } else {
                    console.warn(
                        '[PAIR-APP] No themes returned from API',
                        themesResult
                    );
                    setThemes([]);
                }

                if (tagsResult.success && tagsResult.data) {
                    console.log('[PAIR-APP] Setting tags:', tagsResult.data);
                    setTags(tagsResult.data);
                    console.log(
                        '[PAIR-APP] Fetched tags count:',
                        tagsResult.data.length
                    );
                } else {
                    console.warn(
                        '[PAIR-APP] No tags returned from API',
                        tagsResult
                    );
                    setTags([]);
                }
            } catch (error) {
                console.error('[PAIR-APP] Failed to fetch data:', error);
                setThemes([]);
                setTags([]);
            } finally {
                setIsLoadingData(false);
            }
        };

        fetchData();
    }, [hasLoadedData]);

    // Rotate processing messages and update progress
    useEffect(() => {
        if (isProcessing) {
            setLoadingProgress(1); // Start at 1%
            let currentProgress = 1;
            let animationFrameId: number;

            // Randomize total duration between 4-6 seconds (4000-6000ms)
            const totalDuration = 4000 + Math.random() * 2000;
            console.log(
                `[PAIR-APP] AI curation will take ${(
                    totalDuration / 1000
                ).toFixed(1)} seconds`
            );

            // Define progress zones with proportional durations
            // The proportions remain the same, but scale to the random total duration
            const progressZones = [
                { start: 1, end: 25, duration: totalDuration * 0.127 }, // ~12.7% of time
                { start: 25, end: 45, duration: totalDuration * 0.164 }, // ~16.4% of time
                { start: 45, end: 65, duration: totalDuration * 0.236 }, // ~23.6% of time (AI processing)
                { start: 65, end: 80, duration: totalDuration * 0.145 }, // ~14.5% of time
                { start: 80, end: 90, duration: totalDuration * 0.164 }, // ~16.4% of time
                { start: 90, end: 96, duration: totalDuration * 0.109 }, // ~10.9% of time
                { start: 96, end: 99, duration: totalDuration * 0.027 }, // ~2.7% of time
                { start: 99, end: 100, duration: totalDuration * 0.009 }, // ~0.9% of time
            ];

            let currentZoneIndex = 0;
            let zoneStartTime = Date.now();

            const updateProgress = () => {
                const now = Date.now();
                const currentZone = progressZones[currentZoneIndex];

                if (!currentZone || !isProcessing) {
                    return;
                }

                const zoneElapsed = now - zoneStartTime;
                const zoneProgress = Math.min(
                    zoneElapsed / currentZone.duration,
                    1
                );

                // Calculate current progress with easing
                const easedProgress =
                    zoneProgress < 0.5
                        ? 2 * zoneProgress * zoneProgress
                        : 1 - Math.pow(-2 * zoneProgress + 2, 2) / 2;

                currentProgress = Math.floor(
                    currentZone.start +
                        (currentZone.end - currentZone.start) * easedProgress
                );

                setLoadingProgress(currentProgress);

                // Move to next zone if current is complete
                if (
                    zoneProgress >= 1 &&
                    currentZoneIndex < progressZones.length - 1
                ) {
                    currentZoneIndex++;
                    zoneStartTime = now;
                }

                // Continue animation if not at 100%
                if (currentProgress < 100) {
                    animationFrameId = requestAnimationFrame(updateProgress);
                }
            };

            // Start the animation
            animationFrameId = requestAnimationFrame(updateProgress);

            // Rotate messages
            const interval = setInterval(() => {
                setCurrentMessageIndex(
                    (prev) => (prev + 1) % PROCESSING_MESSAGES.length
                );
            }, 1500);

            return () => {
                clearInterval(interval);
                if (animationFrameId) {
                    cancelAnimationFrame(animationFrameId);
                }
            };
        } else {
            // Reset progress when not processing
            setLoadingProgress(0);
        }
    }, [isProcessing]);

    const handleThemeToggle = (theme: Theme) => {
        setSelectedThemes((prev) => {
            const isSelected = prev.some((t) => t.id === theme.id);
            if (isSelected) {
                return prev.filter((t) => t.id !== theme.id);
            } else {
                // Limit to 5 themes
                if (prev.length >= 5) {
                    return prev;
                }
                return [...prev, theme];
            }
        });
    };

    const handleTagToggle = (tag: Tag) => {
        setSelectedTags((prev) => {
            const isSelected = prev.some((t) => t.name === tag.name);
            if (isSelected) {
                return prev.filter((t) => t.name !== tag.name);
            } else {
                // Limit to 5 tags
                if (prev.length >= 5) {
                    return prev;
                }
                return [...prev, tag];
            }
        });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        // Don't process if already processing
        if (isProcessing) {
            return;
        }

        // Handle "I'm Feeling Lucky" case (nothing selected)
        if (
            selectedThemes.length === 0 &&
            selectedTags.length === 0 &&
            !searchText.trim()
        ) {
            // Track "I'm Feeling Lucky" submission
            analytics.trackEngagement('ai_curation_feeling_lucky', {
                hasSearch: false,
                searchText: null,
                themeCount: 0,
                themes: [],
                tagCount: 0,
                tags: [],
                isFeelingLucky: true,
            });

            // Pass empty values to trigger random selection
            onSubmit('', [], []);
            return;
        }

        // Regular flow with selections
        // Track A/B test conversion when user submits
        trackPromptConversion('submitted', {
            has_search: !!searchText.trim(),
            theme_count: selectedThemes.length,
            tag_count: selectedTags.length,
            search_length: searchText.length,
        });

        // Track interaction
        if (selectedThemes.length > 0 || selectedTags.length > 0) {
            analytics.trackEvent('feature_interaction', {
                interaction_type: 'curation_submitted',
                theme_count: selectedThemes.length,
                tag_count: selectedTags.length,
            });
        }

        // Track AI curation submission with full context
        analytics.trackEngagement('ai_curation_submitted', {
            hasSearch: !!searchText.trim(),
            searchText: searchText.trim() || null,
            themeCount: selectedThemes.length,
            themes: selectedThemes.map((t) => t.name),
            themeIds: selectedThemes.map((t) => t.id),
            tagCount: selectedTags.length,
            tags: selectedTags.map((t) => t.name),
            isFeelingLucky: false,
        });

        // Pass the search text, themes, and tags
        onSubmit(searchText, selectedThemes, selectedTags);
    };

    return (
        <div
            className="bg-gray-900/90 backdrop-blur-xl rounded-2xl border border-gray-600 shadow-2xl w-full max-w-4xl mx-auto overflow-hidden flex flex-col max-h-[85vh] sm:max-h-[90vh]"
            translate="no"
        >
            <AnimatePresence mode="wait">
                {!isProcessing ? (
                    <motion.form
                        key="form"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.4 }}
                        onSubmit={handleSubmit}
                        className="flex flex-col flex-1 min-h-0"
                        translate="no"
                    >
                        {/* Scrollable Content */}
                        <div
                            className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8 space-y-6 overscroll-contain"
                            onWheel={(e) => {
                                const element = e.currentTarget;
                                const isAtTop = element.scrollTop === 0;
                                const isAtBottom =
                                    Math.abs(
                                        element.scrollHeight -
                                            element.scrollTop -
                                            element.clientHeight
                                    ) < 1;

                                // Prevent scroll propagation when at boundaries
                                if (
                                    (isAtTop && e.deltaY < 0) ||
                                    (isAtBottom && e.deltaY > 0)
                                ) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }
                            }}
                            onTouchMove={(e) => {
                                const element = e.currentTarget;
                                const isAtTop = element.scrollTop === 0;
                                const isAtBottom =
                                    Math.abs(
                                        element.scrollHeight -
                                            element.scrollTop -
                                            element.clientHeight
                                    ) < 1;

                                // Prevent scroll propagation on touch devices
                                if (
                                    (isAtTop && element.scrollTop <= 0) ||
                                    (isAtBottom &&
                                        element.scrollTop +
                                            element.clientHeight >=
                                            element.scrollHeight)
                                ) {
                                    e.preventDefault();
                                }
                            }}
                        >
                            {/* Header */}
                            <div className="text-center">
                                <h3 className="text-xl font-semibold text-white mb-2">
                                    Customize Your Art Experience
                                </h3>
                                <p className="text-sm text-gray-400">
                                    Select at least one genre to personalize
                                    your experience
                                </p>
                            </div>

                            {/* Themes Section */}
                            <div className="space-y-4">
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="text-sm font-medium text-gray-400">
                                        Themes
                                    </h4>
                                    <span className="text-xs text-gray-500">
                                        {selectedThemes.length}/5
                                    </span>
                                </div>
                                <div>
                                    {isLoadingData ? (
                                        <div className="grid grid-cols-2 gap-4">
                                            {[1, 2, 3, 4].map((i) => (
                                                <div
                                                    key={i}
                                                    className="aspect-square bg-gray-800/50 rounded-xl animate-pulse"
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-4">
                                            {themes.map((theme, index) => (
                                                <ThemeCard
                                                    key={theme.id}
                                                    theme={theme}
                                                    isSelected={selectedThemes.some(
                                                        (t) => t.id === theme.id
                                                    )}
                                                    onToggle={() =>
                                                        handleThemeToggle(theme)
                                                    }
                                                    index={index}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Tags Section */}
                            <div className="space-y-4">
                                {/* Tags Header */}
                                <div className="text-center">
                                    <h3 className="text-lg font-semibold text-white mb-1">
                                        Find your inspiration
                                    </h3>
                                    <p className="text-sm text-gray-400 mb-2">
                                        More art directions to explore
                                    </p>
                                    <div className="flex justify-center">
                                        <span className="text-xs text-gray-500">
                                            {selectedTags.length}/5
                                        </span>
                                    </div>
                                </div>

                                <div>
                                    {isLoadingData ? (
                                        <div className="flex flex-wrap gap-2">
                                            {[1, 2, 3, 4, 5, 6].map((i) => (
                                                <div
                                                    key={i}
                                                    className="h-10 w-24 bg-gray-800/50 rounded-full animate-pulse"
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="flex flex-wrap gap-2">
                                            {tags.map((tag, index) => (
                                                <TagBubble
                                                    key={tag.name}
                                                    tag={tag}
                                                    isSelected={selectedTags.some(
                                                        (t) =>
                                                            t.name === tag.name
                                                    )}
                                                    onToggle={() =>
                                                        handleTagToggle(tag)
                                                    }
                                                    index={index}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Sticky Footer with Search Bar and Button */}
                        <div
                            className="relative bg-gray-900 pt-6 pb-4 px-4 sm:px-6 md:px-8"
                            style={{
                                paddingBottom:
                                    'max(1rem, env(safe-area-inset-bottom))',
                            }}
                        >
                            {/* Gradient Feather Overlay - positioned at top of footer */}
                            <div className="pointer-events-none absolute -top-12 left-0 right-0 h-12 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />
                            {/* Floating Search Bar with Voice Button */}
                            <div className="mb-4">
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="flex items-center gap-2"
                                >
                                    <div className="relative flex-1">
                                        <textarea
                                            ref={textareaRef}
                                            rows={1}
                                            value={searchText}
                                            onChange={(e) => {
                                                setSearchText(e.target.value);
                                                adjustTextareaHeight(e.target);
                                            }}
                                            placeholder={
                                                placeholderTexts[
                                                    promptVariant
                                                ] || placeholderTexts.control
                                            }
                                            className={classNames(
                                                'w-full px-4 py-2 bg-gray-800/70 text-white rounded-2xl text-sm',
                                                'border transition-all duration-300 resize-none overflow-hidden',
                                                'placeholder-gray-400',
                                                'focus:outline-none focus:ring-2 focus:ring-purple-500/50',
                                                searchText
                                                    ? 'border-purple-500/50'
                                                    : 'border-gray-600 hover:border-gray-500'
                                            )}
                                            style={{
                                                minHeight: '2.5rem',
                                                maxHeight: '8rem',
                                            }}
                                        />
                                        {searchText && (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setSearchText('')
                                                }
                                                className="absolute right-4 top-2.5 text-gray-400 hover:text-white transition-colors"
                                            >
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M6 18L18 6M6 6l12 12"
                                                    />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                    {/* Voice input button - separate from search input */}
                                    {voiceSupported &&
                                        microphonePermission !== 'denied' && (
                                            <div className="relative flex-shrink-0 group">
                                                {/* Active filters indicator during recording */}
                                                {listening &&
                                                    (selectedThemes.length >
                                                        0 ||
                                                        selectedTags.length >
                                                            0) && (
                                                        <motion.div
                                                            initial={{
                                                                opacity: 0,
                                                                scale: 0.8,
                                                            }}
                                                            animate={{
                                                                opacity: 1,
                                                                scale: 1,
                                                            }}
                                                            exit={{
                                                                opacity: 0,
                                                                scale: 0.8,
                                                            }}
                                                            className="absolute -top-8 right-0 text-xs text-purple-400 bg-purple-900/50 px-2 py-1 rounded-full whitespace-nowrap border border-purple-500/30"
                                                        >
                                                            +{' '}
                                                            {
                                                                selectedThemes.length
                                                            }{' '}
                                                            themes,{' '}
                                                            {
                                                                selectedTags.length
                                                            }{' '}
                                                            tags
                                                        </motion.div>
                                                    )}

                                                {/* Tooltip for permission prompt state */}
                                                {microphonePermission ===
                                                    'prompt' && (
                                                    <div className="absolute -top-10 right-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                                        <div className="bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap border border-gray-600">
                                                            Click to enable
                                                            microphone
                                                        </div>
                                                    </div>
                                                )}

                                                <button
                                                    type="button"
                                                    onClick={toggleListening}
                                                    className={classNames(
                                                        'w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200',
                                                        listening
                                                            ? 'bg-red-500 text-white animate-pulse'
                                                            : 'bg-gray-800/70 text-gray-400 hover:text-white hover:bg-gray-700/70 border border-gray-600'
                                                    )}
                                                >
                                                    <svg
                                                        className="w-5 h-5"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                        )}

                                    {/* Show disabled button with help text when permission denied */}
                                    {voiceSupported && permissionDenied && (
                                        <div className="relative flex-shrink-0 group">
                                            {/* Help tooltip */}
                                            <div className="absolute -top-16 right-0 w-56 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                                <div className="bg-gray-800 text-white text-xs rounded px-3 py-2 border border-gray-600">
                                                    <div className="font-semibold mb-1">
                                                        Microphone blocked
                                                    </div>
                                                    <div className="text-gray-300">
                                                        Click the lock icon in
                                                        your browser's address
                                                        bar to enable microphone
                                                        access
                                                    </div>
                                                </div>
                                            </div>

                                            <button
                                                type="button"
                                                disabled
                                                className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-900/50 text-gray-600 border border-gray-700 cursor-not-allowed opacity-50"
                                            >
                                                <svg
                                                    className="w-5 h-5"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    )}
                                </motion.div>
                            </div>

                            {/* Submit button */}
                            <div>
                                <ShimmerButton
                                    type="submit"
                                    disabled={false}
                                    className="w-full"
                                >
                                    Curate art for me
                                </ShimmerButton>
                            </div>
                        </div>
                    </motion.form>
                ) : (
                    <motion.div
                        key="processing"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.4 }}
                        className="py-12 text-center space-y-8"
                        translate="no"
                    >
                        {/* Processing animation */}
                        <div className="relative w-32 h-32 mx-auto">
                            {/* Outer ring */}
                            <motion.div
                                className="absolute inset-0 rounded-full border-4 border-purple-500/20"
                                animate={{ rotate: 360 }}
                                transition={{
                                    duration: 3,
                                    repeat: Infinity,
                                    ease: 'linear',
                                }}
                            />

                            {/* Middle ring */}
                            <motion.div
                                className="absolute inset-4 rounded-full border-4 border-pink-500/30"
                                animate={{ rotate: -360 }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: 'linear',
                                }}
                            />

                            {/* Inner orb */}
                            <motion.div
                                className="absolute inset-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
                                animate={{
                                    scale: [1, 1.2, 1],
                                    opacity: [0.5, 0.8, 0.5],
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: 'easeInOut',
                                }}
                            />

                            {/* Center percentage */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-2xl font-bold text-white">
                                    {loadingProgress}%
                                </span>
                            </div>
                        </div>

                        {/* Progress bar */}
                        <div className="w-full max-w-sm mx-auto mt-6">
                            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                                <motion.div
                                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                                    initial={{ width: '0%' }}
                                    animate={{ width: `${loadingProgress}%` }}
                                    transition={{ duration: 0.3 }}
                                />
                            </div>
                        </div>

                        {/* Processing message */}
                        <AnimatePresence mode="wait">
                            <motion.p
                                key={currentMessageIndex}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.3 }}
                                className="text-xl text-gray-300 font-medium"
                            >
                                {PROCESSING_MESSAGES[currentMessageIndex]}
                            </motion.p>
                        </AnimatePresence>

                        {/* User's input preview */}
                        <div className="max-w-md mx-auto">
                            <p className="text-sm text-gray-500 mb-2">
                                Processing your preferences:
                            </p>
                            {searchText && (
                                <p className="text-gray-300 italic mb-3">
                                    &ldquo;{searchText}&rdquo;
                                </p>
                            )}
                            <div className="space-y-3">
                                {selectedThemes.length > 0 && (
                                    <div>
                                        <p className="text-xs text-gray-500 mb-2">
                                            Themes:
                                        </p>
                                        <div className="flex flex-wrap gap-2 justify-center">
                                            {selectedThemes.map((theme) => (
                                                <span
                                                    key={theme.id}
                                                    className="px-3 py-1 rounded-full bg-purple-600/20 border border-purple-500/50 text-sm text-purple-300"
                                                >
                                                    {theme.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {selectedTags.length > 0 && (
                                    <div>
                                        <p className="text-xs text-gray-500 mb-2">
                                            Tags:
                                        </p>
                                        <div className="flex flex-wrap gap-2 justify-center">
                                            {selectedTags.map((tag) => (
                                                <span
                                                    key={tag.name}
                                                    className="px-3 py-1 rounded-full bg-blue-600/20 border border-blue-500/50 text-sm text-blue-300"
                                                >
                                                    {tag.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
